import {useQuery} from "@apollo/client";
import {useState} from "react";

import {LoadingContainer} from "@/components/placeholder/LoadingContainer.tsx";
import {Sorting} from "@/components/table/Table.types.ts";
import {usePagination} from "@/lib/hooks/usePagination.ts";
import {GQL_RUM_SEGMENT_DATA, RUMHeaderInput, RUMSegmentsDataResponse} from "@/lib/queries/rum.ts";
// @ts-expect-error legacy
import DataTable from "@/pages/rum/v2/details/graphs/DataTable.jsx";
// @ts-expect-error legacy
import LoadTimeWorldMap from "@/pages/rum/v2/details/graphs/LoadTimeWorldMap.jsx";
import {RUMGraphHeader} from "@/pages/rum/v2/details/graphs/RumGraphHeader.tsx";
import {RUMSection, SectionBtnGroup} from "@/pages/rum/v2/details/graphs/segments/SectionBtnGroup.tsx";
import {SectionSegmentDonut} from "@/pages/rum/v2/details/graphs/segments/SectionSegmentDonut.tsx";
import {SectionSegmentsTable} from "@/pages/rum/v2/details/graphs/segments/SectionSegmentsTable.tsx";
import {getDefaultSorting, getSectionDef} from "@/pages/rum/v2/details/graphs/segments/tools.ts";
// @ts-expect-error legacy
import SegmentsTrendGraph from "@/pages/rum/v2/details/graphs/SegmentsTrendGraph.jsx";
import {RUMChartTab, RUMChartTabParams} from "@/pages/rum/v2/details/graphs/types.ts";
import {ReportSpec} from "@/pages/rum/v2/details/ReportDefType.ts";
import {Colors} from "@/pages/rum/v2/RumColors.ts";

const SEGMENT_COLOURS = [Colors.green, Colors.blue, Colors.yellowOrange, Colors.red, Colors.turquoise, Colors.red];

interface SectionSegmentsProps {
  checkName: string;
  reportSpec: ReportSpec;
  payload: RUMHeaderInput;
  id: number;
  tab: RUMChartTab;
}

export const SectionSegments = ({id, checkName, tab, reportSpec, payload}: SectionSegmentsProps) => {
  const [section, setSection] = useState<RUMSection>(RUMSection.URL);
  const [drilldown, setDrilldown] = useState<string | null>(null);
  const {pagination, setPagination} = usePagination({useUrlParams: false});
  // it is important to keep default sorting to null, so sorting resets properly when switching tabs
  const [sorting, setSorting] = useState<Sorting | null>(null);
  const tabSorting = getDefaultSorting(tab);
  const qSection = (section: RUMSection, drilldown: string | null) => {
    switch (section) {
      case RUMSection.DEVICE:
        return "device";
      case RUMSection.PLATFORM:
        return "platform";
      case RUMSection.BROWSER:
        return drilldown ? "browserVersion" : "browser";
      case RUMSection.COUNTRY:
        return drilldown ? "countryRegion" : "country";
      default:
        return section;
    }
  };
  const {data, loading, error, previousData} = useQuery<RUMSegmentsDataResponse>(GQL_RUM_SEGMENT_DATA, {
    variables: {
      id: id,
      tab: RUMChartTabParams[tab],
      section: qSection(section, drilldown),
      payload: {
        ...payload,
        ...pagination,
        ...(drilldown
          ? {
              drillDown: {
                param: section === RUMSection.COUNTRY ? "country" : "browser",
                value: drilldown,
              },
            }
          : {}),
        sort: {
          key: sorting?.column ?? tabSorting.column,
          dir: sorting?.direction ?? tabSorting.direction,
        },
      },
    },
  });

  if (error) {
    console.log(error);
  }

  if (loading) {
    return <LoadingContainer />;
  }

  if (!data?.rumPageLoadSegment.data) {
    return null;
  }
  const tableData = data?.rumPageLoadSegment.data;
  const sectionDef = getSectionDef(reportSpec, section);
  // drill down does not support charts, display previous version if available
  // table always gets latest data
  const {trend, donut, map, breakdown} = drilldown ? previousData?.rumPageLoadSegment.data || tableData : tableData;
  return (
    <>
      <hr className="my-4" />
      <div className="row">
        <div className="col-12 d-flex mb-4">
          <RUMGraphHeader title={`${reportSpec.name} by segment`} supportLink={reportSpec.doc} />
          <SectionBtnGroup
            section={section}
            className="ms-auto"
            onSectionChange={(s) => {
              setSection(s);
              setDrilldown(null);
            }}
          />
        </div>
        {(trend || donut) && (
          <div className="d-flex my-3">
            <div className="w-75">
              <SegmentsTrendGraph
                checkName={checkName}
                key="seg-map"
                data={trend}
                def={sectionDef.trend}
                colors={[Colors.ghostBlue].concat(SEGMENT_COLOURS)}
                maxSeries={5}
              />
            </div>
            <SectionSegmentDonut
              checkName={checkName}
              reportSpec={reportSpec}
              section={section}
              donut={donut}
              colors={SEGMENT_COLOURS}
            />
          </div>
        )}
        {map && (
          <LoadTimeWorldMap
            checkName={checkName}
            key="seg-map"
            data={map}
            color={sectionDef.map.color}
            shade={sectionDef.map.shade}
          />
        )}
        {breakdown && (
          <div className="mb-2">
            <h5>{sectionDef.breakdownTable.title}</h5>
            <DataTable
              key="seg-data-tbl-breakdown"
              def={sectionDef.breakdownTable.columns}
              data={breakdown}
              sort={{dir: null, key: null}}
            />
          </div>
        )}
        {tableData.table && (
          <div className="col-12 mt-3">
            {drilldown && (
              <div className="mb-3 d-flex align-items-center">
                <button className="btn btn-link btn-sm p-0 text-decoration-none" onClick={() => setDrilldown(null)}>
                  {section === RUMSection.COUNTRY ? "Country" : "Browser"}
                  {" > "}
                </button>
                <small className="ms-1">{drilldown}</small>
              </div>
            )}
            <SectionSegmentsTable
              section={section}
              rows={tableData.table}
              sorting={sorting ?? tabSorting}
              onSortingChange={setSorting}
              drilldown={drilldown}
              setDrilldown={setDrilldown}
              pagination={pagination}
              totalRows={tableData.table_total_rows}
              updatePagination={setPagination}
            />
          </div>
        )}
      </div>
    </>
  );
};
